// ============================================================================
// Mobile form view — one field at a time (Dropbox Sign "Form View" /
// DocuSeal step-by-step model).
//
// On a phone, a letter-size page rendered to fit the screen makes a signature
// field roughly a fingernail wide, and the signer ends up pinch-zooming to
// hit it. This view removes the document from the interaction entirely: one
// question per screen, full-width controls, no zoom. The document is one tap
// away and the signer can switch back at any point — some people genuinely
// want to see what they are signing in place, and they should be able to.
// ============================================================================

import { ArrowLeft, ArrowRight, Check, FileText } from 'lucide-react';
import type { EnvSignerMeta } from './envelope-lib';
import { FIELD_LABEL } from './signer-field';

type SignerField = EnvSignerMeta['fields'][number];

/** Human prompt per field type — a question, not a label. */
const PROMPT: Record<string, string> = {
  signature: 'Add your signature',
  initials: 'Add your initials',
  date: "Today's date",
  name: 'Your full name',
  email: 'Your email address',
  text: 'Fill this in',
  checkbox: 'Tick to confirm',
};

export interface FormViewProps {
  fields: SignerField[];
  /**
   * The signer's editable state, owned by envelope-signer.tsx. It is SEEDED
   * from each field's stored value when the fields arrive (AFB-0075 / PR 5),
   * so a merge-prefilled field shows up here already answered and with a
   * "Change" affordance — this view holds no state of its own and must not
   * start one, or the two copies would disagree the moment either changed.
   */
  values: Record<string, string | boolean>;
  index: number;
  isDone: (f: SignerField) => boolean;
  onIndexChange: (i: number) => void;
  onActivate: (f: SignerField) => void;
  onToggleCheckbox: (f: SignerField) => void;
  onShowDocument: () => void;
  remainingRequired: number;
  canFinish: boolean;
  finishing: boolean;
  onFinish: () => void;
}

export function FormView({
  fields, values, index, isDone, onIndexChange, onActivate, onToggleCheckbox,
  onShowDocument, remainingRequired, canFinish, finishing, onFinish,
}: FormViewProps) {
  const field = fields[index];
  if (!field) return null;

  const value = values[field.field_id];
  const done = isDone(field);
  const isImage = typeof value === 'string' && value.startsWith('data:image/');
  const atLast = index >= fields.length - 1;

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-2xl flex-col p-4 pb-36">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-tcs-text-muted">
          Question {index + 1} of {fields.length}
        </p>
        <button
          type="button"
          onClick={onShowDocument}
          className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-tcs-accent-ink"
        >
          <FileText className="h-4 w-4" aria-hidden />
          See the document
        </button>
      </div>

      <div className="mt-1 h-1.5 overflow-hidden rounded bg-tcs-border">
        <div
          className="h-full rounded bg-tcs-accent-ink transition-all"
          style={{ width: `${((index + 1) / fields.length) * 100}%` }}
        />
      </div>

      <section
        aria-live="polite"
        className="mt-6 flex-1 rounded-lg border border-tcs-border bg-tcs-raised p-5"
      >
        <h2 className="text-lg font-semibold text-tcs-text">
          {PROMPT[field.field_type] ?? FIELD_LABEL[field.field_type]}
          {field.required && (
            <span className="ml-1 font-bold text-st-danger-fg" aria-hidden>*</span>
          )}
        </h2>
        <p className="mt-1 text-sm text-tcs-text-muted">
          {field.required ? 'Required' : 'Optional — you can skip this'}
          {' · page '}{field.page}
        </p>

        <div className="mt-5">
          {field.field_type === 'checkbox' ? (
            <label className="flex min-h-[56px] cursor-pointer items-center gap-3 rounded-md border border-tcs-border px-4">
              <input
                type="checkbox"
                checked={value === true}
                onChange={() => onToggleCheckbox(field)}
                className="h-6 w-6"
              />
              <span className="text-base text-tcs-text">Yes</span>
            </label>
          ) : done ? (
            <div className="rounded-md border-2 border-st-ok-border-strong bg-st-ok-bg p-4 text-center">
              {isImage ? (
                <img
                  src={value as string}
                  alt="Your signature"
                  className="mx-auto max-h-20"
                />
              ) : (
                <p className="break-words text-lg font-medium text-st-ok-fg-strong">
                  {String(value)}
                </p>
              )}
              <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-st-ok-fg-strong">
                <Check className="h-3.5 w-3.5" aria-hidden /> Done
              </p>
              <button
                type="button"
                onClick={() => onActivate(field)}
                className="mt-2 block w-full text-sm text-tcs-accent-ink underline"
              >
                Change
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onActivate(field)}
              className="min-h-[56px] w-full rounded-md bg-tcs-accent-ink px-4 text-base font-semibold text-tcs-accent-on"
            >
              {field.field_type === 'signature' || field.field_type === 'initials'
                ? 'Tap to sign'
                : field.field_type === 'text'
                  ? 'Tap to type'
                  : 'Tap to fill'}
            </button>
          )}
        </div>
      </section>

      {/* Sticky step navigation, thumb-reachable. */}
      <div className="fixed inset-x-0 bottom-0 border-t border-tcs-border bg-tcs-raised p-3">
        <div className="mx-auto flex max-w-2xl items-center gap-2">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => onIndexChange(index - 1)}
            className="inline-flex min-h-[48px] items-center gap-1 rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Back
          </button>
          <p className="min-w-0 flex-1 text-center text-xs text-tcs-text-muted">
            {remainingRequired === 0
              ? 'All required questions answered'
              : `${remainingRequired} required left`}
          </p>
          {atLast || canFinish ? (
            <button
              type="button"
              onClick={onFinish}
              disabled={finishing}
              aria-disabled={!canFinish}
              className={`min-h-[48px] rounded-md px-4 text-sm font-semibold text-tcs-accent-on ${
                canFinish ? 'bg-tcs-accent-ink' : 'bg-tcs-accent-ink/40'
              }`}
            >
              {finishing ? 'Sealing…' : 'Finish'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onIndexChange(index + 1)}
              className="inline-flex min-h-[48px] items-center gap-1 rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text"
            >
              Next <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
